import { useEffect, useState } from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import api, {
  withTenantContextSnapshot,
  type TenantContextSnapshot,
} from "@/lib/api";
import type { ApiEnvelope } from "@/lib/types";
import { useI18n } from "@/lib/i18n";
import {
  heldAssetAvailability,
  normalizeAssetAvailability,
  type AssetAvailability,
} from "./lib/assetAvailability";
import {
  normalizePlannerGarage,
  type PlannerGarage,
  type PlannerGarageAsset,
} from "./lib/plannerGarage";
import type {
  BatchCreatePayload,
  CatalogBrowseResponse,
  DashboardSummary,
  ExternalLayer,
  FrontierEntryContext,
  GateEvaluationResponse,
  OpsCustomer,
  GeoPlaceDUPayload,
  GeoPlaceDUResult,
  GeoPlacementStatus,
  GeoUnplaceDUPayload,
  GeoUnplaceDUResult,
  GeoUpdateDUPayload,
  GeoUpdateDUResult,
  GeoProviderRegistryResponse,
  GeoSearchResponse,
  LayerCatalogEntry,
  MaterializationResult,
  OpsBatch,
  OpsCoexistenceRule,
  OpsCommissioningRecord,
  OpsCommissioningSummary,
  OpsDeploymentUnit,
  OpsGeoAsset,
  OpsGeoBaseline,
  OpsMaintenanceSummary,
  PlanningScopeAdjustmentPayload,
  PlanningScopeAdjustmentResult,
  OpsProject,
  OpsScope,
  OpsScopeLine,
  OpsSite,
  OpsTwinUnit,
  ProjectRuntimeStatus,
  ProjectNetworkTopology,
  ProjectRfBandRegion,
  ProjectWorkspaceSummary,
  KodiakPublication,
  KodiakPublicationList,
  KodiakPublicationPreview,
  SubstitutionResult,
  DeviceTelemetry,
  TelemetryHistory,
  TelemetryValue,
  PlatformHealth,
  SimulationStatus,
  FleetHierarchy,
  FleetAggregation,
  ProjectZone,
} from "./types";
import { withOptimisticGeoAssetCoordinates } from "./lib/geo-asset-coordinates";
import { fetchAllProjectZones } from "./lib/project-zone-pages";
import { FRONTIER_HOME_KEY, GATE_RECONCILE_KEY } from "./home/api";
import { invalidateFrontierAlternatives } from "./frontier-f1b/queries";

export {
  useFrontierRevisionDiff,
  useFrontierRevisions,
  useFrontierStages,
  usePrepareFrontierRevision,
  useValidateFrontierRevision,
} from "./frontier-e09/queries";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------
const OPS_AVAILABLE_ASSETS_KEY = "ops-available-assets";
const OPS_ASSET_ASSIGNMENTS_KEY = "ops-asset-assignments";
const OPS_DASHBOARD_KEY = "ops-dashboard-summary";
const OPS_PROJECTS_KEY = "ops-projects";
export const OPS_WORKSPACE_SUMMARY_KEY = "ops-workspace-summary";
const OPS_SCOPES_KEY = "ops-scopes";
const OPS_SCOPE_LINES_KEY = "ops-scope-lines";
const OPS_SITES_KEY = "ops-sites";
const OPS_GEO_ASSETS_KEY = "ops-geo-assets";
const OPS_GEO_BASELINES_KEY = "ops-geo-baselines";
const OPS_TWIN_UNITS_KEY = "ops-twin-units";
const OPS_COEXISTENCE_KEY = "ops-coexistence-rules";
const OPS_BATCHES_KEY = "ops-batches";
const OPS_PROJECT_DUS_KEY = "ops-project-dus";
const OPS_COMMISSIONING_SUMMARY_KEY = "ops-commissioning-summary";
const OPS_COMMISSIONING_RECORDS_KEY = "ops-commissioning-records";
const OPS_MAINTENANCE_SUMMARY_KEY = "ops-maintenance-summary";
const OPS_GEO_PLACEMENT_STATUS_KEY = "ops-geo-placement-status";
const OPS_PROJECT_NETWORK_TOPOLOGY_KEY = "ops-project-network-topology";
const TELEMETRY_LATEST_KEY = "telemetry-device-latest";
const TELEMETRY_HISTORY_KEY = "telemetry-device-history";
const TELEMETRY_FLEET_KEY = "telemetry-fleet-latest";
const PLATFORM_HEALTH_KEY = "platform-health";
const SIMULATION_STATUS_KEY = "simulation-status";
// Query key value retained for cache invalidation compatibility with older
// Planning consumers that referenced the historical catalog browse cache.
const OPS_FACTORY_CATALOG_BROWSE_KEY = "ops-catalog-browse";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function toParams(
  f?: Record<string, string | undefined>,
): Record<string, string> {
  const p: Record<string, string> = {};
  if (!f) return p;
  for (const [k, v] of Object.entries(f)) {
    if (v !== undefined && v !== null && v !== "") p[k] = String(v);
  }
  return p;
}

// ---------------------------------------------------------------------------
// Dashboard Summary
// ---------------------------------------------------------------------------
export function useDashboardSummary() {
  return useQuery({
    queryKey: [OPS_DASHBOARD_KEY],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<DashboardSummary>>(
        "/ops/dashboard-summary/",
      );
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// Project List
// ---------------------------------------------------------------------------
export interface OpsProjectFilters {
  operational_view_state?: string;
  search?: string;
}

export function useOpsProjects(filters?: OpsProjectFilters) {
  return useQuery({
    queryKey: [OPS_PROJECTS_KEY, filters],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<OpsProject[]>>(
        "/ops/projects/",
        { params: toParams(filters as Record<string, string | undefined>) },
      );
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// Workspace Summary (single project)
// ---------------------------------------------------------------------------
export function useProjectWorkspaceSummary(projectId: string | undefined) {
  return useQuery({
    queryKey: [OPS_WORKSPACE_SUMMARY_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<ProjectWorkspaceSummary>>(
        `/ops/projects/${projectId}/workspace-summary/`,
      );
      return data;
    },
    enabled: !!projectId,
  });
}

// ---------------------------------------------------------------------------
// Scopes for a project (reuses /projects/scopes/ from SAK)
// ---------------------------------------------------------------------------
export function useProjectScopes(projectId: string | undefined) {
  return useQuery({
    queryKey: [OPS_SCOPES_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<OpsScope[]>>(
        "/projects/scopes/",
        { params: { project: projectId } },
      );
      return data;
    },
    enabled: !!projectId,
  });
}

// ---------------------------------------------------------------------------
// Scope Lines for a scope (reuses /projects/scope-lines/ from SAK)
// ---------------------------------------------------------------------------
export function useScopeLines(scopeId: string | undefined) {
  return useQuery({
    queryKey: [OPS_SCOPE_LINES_KEY, scopeId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<OpsScopeLine[]>>(
        "/projects/scope-lines/",
        { params: { scope: scopeId } },
      );
      return data;
    },
    enabled: !!scopeId,
  });
}

// ---------------------------------------------------------------------------
// Sites for a project (reuses /projects/sites/ from SAK)
// ---------------------------------------------------------------------------
export function useProjectSites(projectId: string | undefined) {
  return useQuery({
    queryKey: [OPS_SITES_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<OpsSite[]>>(
        "/projects/sites/",
        { params: { project: projectId } },
      );
      return data;
    },
    enabled: !!projectId,
  });
}

// ---------------------------------------------------------------------------
// Scope Materialization
// ---------------------------------------------------------------------------
export function useMaterializeScope(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (scopeId: string) => {
      const { data } = await api.post<ApiEnvelope<MaterializationResult>>(
        `/ops/projects/${projectId}/scope-materializations/`,
        { scope_id: scopeId },
      );
      return data;
    },
    onSuccess: () => {
      void refreshProjectGates(qc, projectId);
      // Invalidate everything that depends on DUs existing
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_SCOPES_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_GEO_PLACEMENT_STATUS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_PROJECT_DUS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_GEO_ASSETS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_DASHBOARD_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Planning Scope Adjustment
// ---------------------------------------------------------------------------
export function useAdjustPlanningScope(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: PlanningScopeAdjustmentPayload) => {
      const { data } = await api.post<PlanningScopeAdjustmentResult>(
        `/ops/projects/${projectId}/planning-scope-adjustments/`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_SCOPES_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_SCOPE_LINES_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_PROJECT_DUS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_GEO_PLACEMENT_STATUS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_GEO_ASSETS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_DASHBOARD_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Update Project
// ---------------------------------------------------------------------------
export function useUpdateProject(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: Partial<{
        name: string;
        description: string;
        start_date: string | null;
        target_date: string | null;
        parent: string | null;
      }>,
    ) => {
      const { data: resp } = await api.patch<ApiEnvelope<OpsProject>>(
        `/ops/projects/${projectId}/`,
        data,
      );
      return resp;
    },
    onSuccess: () => {
      void refreshProjectGates(qc, projectId);
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_PROJECTS_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Project Transition — suspend, resume or close by hand (R05b)
// ---------------------------------------------------------------------------
/**
 * Sends one of the targets `workspace-summary/` lists in
 * `available_transitions`; every other lifecycle change follows the gate.
 */
export function useTransitionProject(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (targetState: string) => {
      const { data } = await api.post<ApiEnvelope<OpsProject>>(
        `/ops/projects/${projectId}/transition/`,
        { target_state: targetState },
      );
      return data;
    },
    // Also on failure: a refusal (409/400) means the project changed
    // meanwhile, so its summary, gates, the Frontier home and the list are
    // read again.
    onSettled: () => {
      void refreshProjectGates(qc, projectId);
      void qc.invalidateQueries({ queryKey: [OPS_PROJECTS_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Gate Evaluation — the 11 gates with what each one still misses (R05/R06)
// ---------------------------------------------------------------------------
export const OPS_GATE_EVALUATION_KEY = "ops-gate-evaluation";

/**
 * The gate advances by itself after a zone, area or design write (O-1): the
 * rail and the workspace summary of that project (of every project when the
 * write does not say which) read it again, and so do the Frontier home, which
 * shows every project's step and state, and the dry run of bringing the steps
 * up to date (read again only if its dialog is open).
 */
export function refreshProjectGates(
  qc: QueryClient,
  projectId?: string,
): Promise<unknown> {
  const scoped = (key: string) => (projectId ? [key, projectId] : [key]);
  return Promise.all([
    qc.invalidateQueries({ queryKey: scoped(OPS_GATE_EVALUATION_KEY) }),
    qc.invalidateQueries({ queryKey: scoped(OPS_WORKSPACE_SUMMARY_KEY) }),
    qc.invalidateQueries({ queryKey: [FRONTIER_HOME_KEY] }),
    qc.invalidateQueries({ queryKey: [GATE_RECONCILE_KEY] }),
  ]);
}

/**
 * What each gate of the project misses. `enabled: false` neither reads nor
 * polls it: a screen that hides the rail does not ask (FP-45).
 */
export function useGateEvaluation(
  projectId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [OPS_GATE_EVALUATION_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<GateEvaluationResponse>>(
        `/ops/projects/${projectId}/gate-evaluation/`,
      );
      return data.data;
    },
    enabled: !!projectId && enabled,
    // What a gate misses changes from other tabs (the validation is
    // confirmed in Revisar y validar), so the rail refreshes on its own.
    refetchInterval: 15_000,
  });
}

// ---------------------------------------------------------------------------
// Wizard Gate Advance
// ---------------------------------------------------------------------------
export interface WizardGateAdvanceResult extends OpsProject {
  direction: "forward" | "backward";
  gate_name?: string;
}

export function useAdvanceWizardGate(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    /** With `expectedGate`, the server refuses (409) if the project moved. */
    mutationFn: async (
      move:
        | "forward"
        | "backward"
        | { direction: "forward" | "backward"; expectedGate: number },
    ) => {
      const body =
        typeof move === "string"
          ? { direction: move }
          : { direction: move.direction, expected_gate: move.expectedGate };
      const { data } = await api.post<ApiEnvelope<WizardGateAdvanceResult>>(
        `/ops/projects/${projectId}/wizard-gate-advance/`,
        body,
      );
      return data;
    },
    // Also on failure: a 409 means the project moved, so every screen reloads it.
    onSettled: () => {
      void refreshProjectGates(qc);
      void qc.invalidateQueries({ queryKey: [OPS_PROJECTS_KEY] });
      void qc.invalidateQueries({ queryKey: ["deployment-review"] });
    },
  });
}

// ---------------------------------------------------------------------------
// GEO Assets for a project
// ---------------------------------------------------------------------------
export function useProjectGeoAssets(projectId: string | undefined) {
  return useQuery({
    queryKey: [OPS_GEO_ASSETS_KEY, projectId],
    queryFn: async ({ signal }) => {
      const { data } = await api.get<ApiEnvelope<OpsGeoAsset[]>>(
        `/ops/projects/${projectId}/geo-assets/`,
        { signal },
      );
      return data;
    },
    enabled: !!projectId,
    staleTime: 1000 * 30, // 30s — invalidated explicitly after placement
  });
}

// ---------------------------------------------------------------------------
// GEO Baselines for a project
// ---------------------------------------------------------------------------
export function useProjectGeoBaselines(projectId: string | undefined) {
  return useQuery({
    queryKey: [OPS_GEO_BASELINES_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<OpsGeoBaseline[]>>(
        "/geo/baselines/",
        { params: { project: projectId } },
      );
      return data;
    },
    enabled: !!projectId,
  });
}

// ---------------------------------------------------------------------------
// Twin Units for a project
// ---------------------------------------------------------------------------
export function useProjectTwinUnits(projectId: string | undefined) {
  return useQuery({
    queryKey: [OPS_TWIN_UNITS_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<OpsTwinUnit[]>>(
        "/twins/units/",
        { params: { project: projectId } },
      );
      return data;
    },
    enabled: !!projectId,
  });
}

// ---------------------------------------------------------------------------
// Coexistence Rules for twin units
// ---------------------------------------------------------------------------
export function useCoexistenceRules(projectId: string | undefined) {
  return useQuery({
    queryKey: [OPS_COEXISTENCE_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<OpsCoexistenceRule[]>>(
        "/twins/coexistence-rules/",
        { params: { project: projectId } },
      );
      return data;
    },
    enabled: !!projectId,
  });
}

// ---------------------------------------------------------------------------
// Runtime Status
// ---------------------------------------------------------------------------
const OPS_RUNTIME_STATUS_KEY = "ops-runtime-status";

export function useProjectRuntimeStatus(projectId: string | undefined) {
  return useQuery({
    queryKey: [OPS_RUNTIME_STATUS_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<ProjectRuntimeStatus>>(
        `/ops/projects/${projectId}/runtime-status/`,
      );
      return data;
    },
    enabled: !!projectId,
    staleTime: 15_000,
  });
}

// ---------------------------------------------------------------------------
// Kodiak publications — SLICE-019 (#423).
//
// The client portal reads only from the active KodiakPublication. These hooks
// back the Frontier "Vista cliente (Kodiak)" tab: list (active + history +
// source), preview (dry run: manifest + diff, persists nothing), publish
// (revision N+1, reason) and revoke (mandatory reason). The #415 boolean
// `client_preview_published` flag and its `/twins/projects/<id>/client-preview/`
// endpoint are legacy: still served, no longer driven from this UI.
// ---------------------------------------------------------------------------
const OPS_KODIAK_PUBLICATIONS_KEY = "ops-kodiak-publications";
const OPS_KODIAK_PREVIEW_KEY = "ops-kodiak-publication-preview";

export function useProjectPublications(projectId: string | undefined) {
  return useQuery({
    queryKey: [OPS_KODIAK_PUBLICATIONS_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<KodiakPublicationList>>(
        `/ops/projects/${projectId}/publications/`,
      );
      return data;
    },
    enabled: !!projectId,
    staleTime: 15_000,
  });
}

export function useProjectPublicationPreview(
  projectId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [OPS_KODIAK_PREVIEW_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<KodiakPublicationPreview>>(
        `/ops/projects/${projectId}/publications/preview/`,
      );
      return data;
    },
    enabled: !!projectId && enabled,
    staleTime: 0,
  });
}

export function usePublishProjectPublication(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload:
        | string
        | {
            reason: string;
            expected_dashboard_version: number;
            expected_manifest_hash?: string;
          },
    ) => {
      const { data } = await api.post<ApiEnvelope<KodiakPublication>>(
        `/ops/projects/${projectId}/publications/`,
        typeof payload === "string" ? { reason: payload } : payload,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_KODIAK_PUBLICATIONS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_KODIAK_PREVIEW_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
    },
  });
}

export function useRevokeProjectPublication(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      publicationId,
      reason,
    }: {
      publicationId: string;
      reason: string;
    }) => {
      const { data } = await api.post<ApiEnvelope<KodiakPublication>>(
        `/ops/projects/${projectId}/publications/${publicationId}/revoke/`,
        { reason },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_KODIAK_PUBLICATIONS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_KODIAK_PREVIEW_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Batches (FieldPackages)
// ---------------------------------------------------------------------------
export function useProjectBatches(projectId: string | undefined) {
  return useQuery({
    queryKey: [OPS_BATCHES_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<OpsBatch[]>>(
        `/ops/projects/${projectId}/batches/`,
      );
      return data;
    },
    enabled: !!projectId,
  });
}

export function useCreateBatch(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: BatchCreatePayload) => {
      const { data } = await api.post<ApiEnvelope<OpsBatch>>(
        `/ops/projects/${projectId}/batches/`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_BATCHES_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
    },
  });
}

export function useDispatchBatch(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (batchId: string) => {
      const { data } = await api.post<ApiEnvelope<OpsBatch>>(
        `/ops/projects/${projectId}/batches/${batchId}/dispatch/`,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_BATCHES_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Project Deployment Units
// ---------------------------------------------------------------------------
export function useProjectDeploymentUnits(projectId: string | undefined) {
  return useQuery({
    queryKey: [OPS_PROJECT_DUS_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<OpsDeploymentUnit[]>>(
        `/ops/projects/${projectId}/deployment-units/`,
      );
      return data;
    },
    enabled: !!projectId,
  });
}

// ---------------------------------------------------------------------------
// Commissioning Summary
// ---------------------------------------------------------------------------
export function useCommissioningSummary(projectId: string | undefined) {
  return useQuery({
    queryKey: [OPS_COMMISSIONING_SUMMARY_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<OpsCommissioningSummary>>(
        `/ops/projects/${projectId}/commissioning-summary/`,
      );
      return data;
    },
    enabled: !!projectId,
  });
}

// ---------------------------------------------------------------------------
// Commissioning Records
// ---------------------------------------------------------------------------
export function useCommissioningRecords(projectId: string | undefined) {
  return useQuery({
    queryKey: [OPS_COMMISSIONING_RECORDS_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<OpsCommissioningRecord[]>>(
        `/ops/projects/${projectId}/commissioning-records/`,
      );
      return data;
    },
    enabled: !!projectId,
  });
}

// ---------------------------------------------------------------------------
// Commissioning Record Transition
// ---------------------------------------------------------------------------
export function useTransitionCommissioningRecord(
  projectId: string,
  recordId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (targetStatus: string) => {
      const { data } = await api.post<ApiEnvelope<OpsCommissioningRecord>>(
        `/ops/projects/${projectId}/commissioning-records/${recordId}/transition/`,
        { target_status: targetStatus },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [OPS_COMMISSIONING_RECORDS_KEY],
      });
      void qc.invalidateQueries({
        queryKey: [OPS_COMMISSIONING_SUMMARY_KEY],
      });
      void qc.invalidateQueries({
        queryKey: [OPS_WORKSPACE_SUMMARY_KEY],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Maintenance Summary
// ---------------------------------------------------------------------------
export function useMaintenanceSummary(projectId: string | undefined) {
  return useQuery({
    queryKey: [OPS_MAINTENANCE_SUMMARY_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<OpsMaintenanceSummary>>(
        `/ops/projects/${projectId}/maintenance-summary/`,
      );
      return data;
    },
    enabled: !!projectId,
  });
}

// ---------------------------------------------------------------------------
// Create Project (legacy /projects/projects/ — used only when no F1A customer
// is in scope; see CreateProjectDialog)
// ---------------------------------------------------------------------------
export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      name: string;
      program?: string;
      parent?: string;
      organization?: string;
      start_date?: string;
      target_date?: string;
    }) => {
      const { data: resp } = await api.post<ApiEnvelope<OpsProject>>(
        "/projects/projects/",
        data,
      );
      return resp;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_PROJECTS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_DASHBOARD_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Frontier F1A — customers in scope (E07) and governed project creation (E08)
// `GET /ops/customers/` lists the customers of the active organization;
// `POST /ops/projects/` creates the project in the customer's workspace with
// its Initial Alternative (FRT-DEC-039). Deny-by-default: a 403 on the
// customers read simply means "no F1A customers in scope" for the dialog.
// ---------------------------------------------------------------------------
export const OPS_CUSTOMERS_KEY = "ops-customers";

export function useOpsCustomers(
  enabled = true,
  context?: TenantContextSnapshot,
) {
  const snapshot = context ?? {
    organization: localStorage.getItem("active_organization_id"),
    workspace: localStorage.getItem("active_workspace_id"),
  };
  return useQuery({
    queryKey: [OPS_CUSTOMERS_KEY, snapshot.organization, snapshot.workspace],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<OpsCustomer[]>>(
        "/ops/customers/",
        withTenantContextSnapshot(snapshot),
      );
      return data;
    },
    enabled,
    retry: false,
  });
}

/**
 * `POST /ops/customers/` — create a customer organization in the active scope
 * (SLICE-044, #488). The backend opens its default workspace in the same
 * transaction and records a `customer.created` governance event, so a
 * successful response means the customer can host a project immediately.
 *
 * The customers list and the frontier home summary are invalidated on success:
 * the create-project dialog reads the same key, so the new customer is
 * selectable the moment this resolves — no manual refresh, no fabricated row
 * written into the cache.
 */
export function useCreateOpsCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string }) => {
      const { data: resp } = await api.post<
        ApiEnvelope<OpsCustomer & { workspace_id: string }>
      >("/ops/customers/", data);
      return resp;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_CUSTOMERS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_DASHBOARD_KEY] });
    },
  });
}

export interface CreateFrontierProjectPayload {
  name: string;
  customer: string;
  entry_context?: FrontierEntryContext;
  target_date?: string;
  need?: string;
  description?: string;
  responsible?: string;
}

export function useCreateFrontierProject(context?: TenantContextSnapshot) {
  const snapshot = context ?? {
    organization: localStorage.getItem("active_organization_id"),
    workspace: localStorage.getItem("active_workspace_id"),
  };
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateFrontierProjectPayload) => {
      const { data: resp } = await api.post<ApiEnvelope<OpsProject>>(
        "/ops/projects/",
        data,
        withTenantContextSnapshot(snapshot),
      );
      return resp;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_PROJECTS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_DASHBOARD_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Ensure Scope (idempotent — auto-creates v1 draft if none exists)
// ---------------------------------------------------------------------------
export function useEnsureScope(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<
        ApiEnvelope<OpsScope & { created: boolean }>
      >(`/ops/projects/${projectId}/ensure-scope/`);
      return data;
    },
    onSuccess: () => {
      void refreshProjectGates(qc, projectId);
      void qc.invalidateQueries({ queryKey: [OPS_SCOPES_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Ensure Site — idempotent root site creation
// ---------------------------------------------------------------------------
export function useEnsureSite(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{
        id: string;
        name: string;
        created: boolean;
      }>(`/ops/projects/${projectId}/ensure-site/`);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_SITES_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Create Scope
// ---------------------------------------------------------------------------
export function useCreateScope() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      project: string;
      version: number;
      metadata?: Record<string, unknown>;
    }) => {
      const { data: resp } = await api.post<ApiEnvelope<OpsScope>>(
        "/projects/scopes/",
        data,
      );
      return resp;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_SCOPES_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Create Scope Line
// ---------------------------------------------------------------------------
export function useCreateScopeLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      scope: string;
      definition?: string;
      definition_ver?: number;
      hardware_variant?: string;
      site: string;
      quantity: number;
      placement?: string;
      stage?: string;
      config_overrides?: Record<string, unknown>;
      priority?: number;
      metadata?: Record<string, unknown>;
    }) => {
      const { data: resp } = await api.post<ApiEnvelope<OpsScopeLine>>(
        "/projects/scope-lines/",
        data,
      );
      return resp;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_SCOPE_LINES_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_SCOPES_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// GEO Connectivity Graph (from SAK GEO API)
// ---------------------------------------------------------------------------
const OPS_GEO_CONNECTIVITY_KEY = "ops-geo-connectivity";

export function useProjectConnectivityGraph(projectId: string | undefined) {
  return useQuery({
    queryKey: [OPS_GEO_CONNECTIVITY_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<unknown[]>>(
        "/sak-geo/connectivity-graphs/",
        { params: { project: projectId } },
      );
      return data;
    },
    enabled: !!projectId,
  });
}

export function useProjectNetworkTopology(
  projectId: string | undefined,
  alternativeId?: string | null,
) {
  return useQuery({
    queryKey: [OPS_PROJECT_NETWORK_TOPOLOGY_KEY, projectId, alternativeId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<ProjectNetworkTopology>>(
        `/ops/projects/${projectId}/network-topology/`,
        {
          params: alternativeId ? { alternative_id: alternativeId } : undefined,
        },
      );
      return data;
    },
    enabled: !!projectId && !!alternativeId,
    staleTime: 1000 * 30,
  });
}

/**
 * Each placed device's LoRaWAN band against the project's country (R16).
 * Keyed under the topology so every placement change refreshes it too.
 */
export function useProjectRfBandRegion(
  projectId: string | undefined,
  alternativeId?: string | null,
) {
  return useQuery({
    queryKey: [
      OPS_PROJECT_NETWORK_TOPOLOGY_KEY,
      projectId,
      alternativeId,
      "rf-band-region",
    ],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<ProjectRfBandRegion>>(
        `/ops/projects/${projectId}/rf-band-region/`,
        { params: { alternative_id: alternativeId } },
      );
      return data;
    },
    enabled: !!projectId && !!alternativeId,
    staleTime: 1000 * 30,
  });
}

// ---------------------------------------------------------------------------
// GEO Placement Status
// ---------------------------------------------------------------------------
export function useGeoPlacementStatus(
  projectId: string | undefined,
  alternativeId?: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [OPS_GEO_PLACEMENT_STATUS_KEY, projectId, alternativeId],
    queryFn: async ({ signal }) => {
      const { data } = await api.get<ApiEnvelope<GeoPlacementStatus>>(
        `/ops/projects/${projectId}/geo-placement-status/`,
        {
          signal,
          params: alternativeId ? { alternative_id: alternativeId } : undefined,
        },
      );
      return data;
    },
    enabled: !!projectId && (options?.enabled ?? true),
    staleTime: 1000 * 30, // 30s — invalidated explicitly after placement
  });
}

export interface FrontierDesignPlacementPayload {
  alternative_id: string;
  product_id: string;
  latitude: number;
  longitude: number;
}

/** Contract of `DELETE /ops/projects/:id/design-placements/`. */
export interface FrontierDesignPlacementRemoval {
  du_id: string;
  removed: boolean;
  /** True when the device carried a Garage binding: the unit went back to stock and the DU is retired, not deleted. */
  retained?: boolean;
  released_assets?: string[];
  idempotent_replay: boolean;
}

/** Contract of `POST /ops/projects/:id/design-placements/` (materialization + placement). */
export interface FrontierDesignPlacementResponse {
  created: boolean;
  idempotent_replay: boolean;
  deployment_units: Array<{
    id: string;
    logical_code: string;
    label: string;
  }>;
  design_placement: { id: string; latitude: number; longitude: number };
}

export function useCreateFrontierDesignPlacement(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: FrontierDesignPlacementPayload) => {
      const { data } = await api.post<
        ApiEnvelope<FrontierDesignPlacementResponse>
      >(`/ops/projects/${projectId}/design-placements/`, payload, {
        headers: { "Idempotency-Key": `frontier-map-${crypto.randomUUID()}` },
      });
      return data;
    },
    onSuccess: () => {
      void refreshProjectGates(qc, projectId);
      void qc.invalidateQueries({ queryKey: [OPS_GEO_PLACEMENT_STATUS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_GEO_ASSETS_KEY] });
      void qc.invalidateQueries({
        queryKey: [OPS_PROJECT_NETWORK_TOPOLOGY_KEY],
      });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
      void qc.invalidateQueries({ queryKey: ["frontier-f1c-workspace"] });
    },
  });
}

export function useRemoveFrontierDesignPlacement(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { alternative_id: string; du_id: string }) => {
      const { data } = await api.delete<
        ApiEnvelope<FrontierDesignPlacementRemoval>
      >(`/ops/projects/${projectId}/design-placements/`, { data: payload });
      return data;
    },
    onSuccess: () => {
      void refreshProjectGates(qc, projectId);
      void qc.invalidateQueries({ queryKey: [OPS_GEO_PLACEMENT_STATUS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_GEO_ASSETS_KEY] });
      void qc.invalidateQueries({
        queryKey: [OPS_PROJECT_NETWORK_TOPOLOGY_KEY],
      });
      // A Garage-bound device returns its unit to stock when removed.
      void qc.invalidateQueries({ queryKey: [OPS_AVAILABLE_ASSETS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_ASSET_ASSIGNMENTS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
      void qc.invalidateQueries({ queryKey: ["frontier-f1c-workspace"] });
    },
  });
}

export interface FrontierDesignDeviceDetailsPayload {
  alternative_id: string;
  du_id: string;
  label: string;
  notes: string;
  /** Omit to keep the stored orientation; null clears it (north). */
  azimuth_deg?: number | null;
  /** Omit to keep the stored installation height; null clears it (role default). */
  mount_height_m?: number | null;
}

export function useUpdateFrontierDesignDeviceDetails(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: FrontierDesignDeviceDetailsPayload) => {
      const { data } = await api.patch<ApiEnvelope<unknown>>(
        `/ops/projects/${projectId}/design-placements/`,
        { ...payload, action: "update_device" },
      );
      return data;
    },
    onSuccess: (_data, payload) => {
      void refreshProjectGates(qc, projectId);
      void qc.invalidateQueries({ queryKey: [OPS_GEO_PLACEMENT_STATUS_KEY] });
      // Orientation and height are read back through topology, which keys the
      // detection range and feeds the network coverage calculation.
      if ("azimuth_deg" in payload || "mount_height_m" in payload) {
        void qc.invalidateQueries({
          queryKey: [OPS_PROJECT_NETWORK_TOPOLOGY_KEY],
        });
      }
    },
  });
}

export function useFinishFrontierDesign(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (alternativeId: string) => {
      const { data } = await api.patch<ApiEnvelope<unknown>>(
        `/ops/projects/${projectId}/design-placements/`,
        { alternative_id: alternativeId },
      );
      return data;
    },
    onSuccess: () => {
      void refreshProjectGates(qc, projectId);
      void qc.invalidateQueries({
        queryKey: ["frontier-f1b", "alternatives", projectId],
      });
      void qc.invalidateQueries({ queryKey: ["frontier-f1c-workspace"] });
    },
  });
}

export function useReopenFrontierDesign(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (alternativeId: string) => {
      const { data } = await api.patch<ApiEnvelope<unknown>>(
        `/ops/projects/${projectId}/design-placements/`,
        { alternative_id: alternativeId, action: "reopen" },
      );
      return data;
    },
    onSuccess: () => {
      void refreshProjectGates(qc, projectId);
      void qc.invalidateQueries({
        queryKey: ["frontier-f1b", "alternatives", projectId],
      });
      void qc.invalidateQueries({ queryKey: [OPS_GEO_PLACEMENT_STATUS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_GEO_ASSETS_KEY] });
      void qc.invalidateQueries({ queryKey: ["frontier-f1c-workspace"] });
    },
  });
}

interface FrontierDesignCopyResponse {
  alternative: { id: string };
  copied_devices: number;
}

export function useCopyFrontierDesign(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      source_alternative_id: string;
      name: string;
    }) => {
      const { data } = await api.put<ApiEnvelope<FrontierDesignCopyResponse>>(
        `/ops/projects/${projectId}/design-placements/`,
        payload,
        {
          headers: {
            "Idempotency-Key": `frontier-copy-${crypto.randomUUID()}`,
          },
        },
      );
      return data.data;
    },
    onSuccess: () => {
      void refreshProjectGates(qc, projectId);
      void qc.invalidateQueries({ queryKey: [OPS_GEO_PLACEMENT_STATUS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_GEO_ASSETS_KEY] });
      void qc.invalidateQueries({
        queryKey: [OPS_PROJECT_NETWORK_TOPOLOGY_KEY],
      });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
      // The copy adds an alternative; the catalogue itself is unchanged.
      void invalidateFrontierAlternatives(qc, projectId);
      void qc.invalidateQueries({ queryKey: ["frontier-f1c-workspace"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Place DU on map
// ---------------------------------------------------------------------------
// Debounced invalidation — when placing multiple devices rapidly, we batch
// the query invalidations to avoid a cascade of 429s (#106).
let _placementInvalidateTimer: ReturnType<typeof setTimeout> | null = null;

export function usePlaceDU(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: GeoPlaceDUPayload) => {
      const { data } = await api.post<ApiEnvelope<GeoPlaceDUResult>>(
        `/ops/projects/${projectId}/geo-place-du/`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      // Debounce: wait 800ms after last placement before refetching.
      // This batches rapid-fire placements into a single refetch cycle.
      if (_placementInvalidateTimer) clearTimeout(_placementInvalidateTimer);
      _placementInvalidateTimer = setTimeout(() => {
        void qc.invalidateQueries({ queryKey: [OPS_GEO_PLACEMENT_STATUS_KEY] });
        void qc.invalidateQueries({ queryKey: [OPS_GEO_ASSETS_KEY] });
        void qc.invalidateQueries({
          queryKey: [OPS_PROJECT_NETWORK_TOPOLOGY_KEY],
        });
        void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
        _placementInvalidateTimer = null;
      }, 800);
    },
  });
}

// ---------------------------------------------------------------------------
// Update DU position (reposition)
// ---------------------------------------------------------------------------
export function useUpdateDUPosition(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: GeoUpdateDUPayload) => {
      const { data } = await api.patch<ApiEnvelope<GeoUpdateDUResult>>(
        `/ops/projects/${projectId}/geo-update-du/`,
        payload,
      );
      return data;
    },
    onMutate: async (payload) => {
      // Optimistic update: immediately update asset coordinates in the cache
      // so topology lines recalculate WITHOUT waiting for the refetch (#112).
      await qc.cancelQueries({ queryKey: [OPS_GEO_ASSETS_KEY, projectId] });
      const prev = qc.getQueryData<ApiEnvelope<OpsGeoAsset[]>>([
        OPS_GEO_ASSETS_KEY,
        projectId,
      ]);
      if (prev?.data) {
        qc.setQueryData<ApiEnvelope<OpsGeoAsset[]>>(
          [OPS_GEO_ASSETS_KEY, projectId],
          {
            ...prev,
            data: prev.data.map((asset) =>
              withOptimisticGeoAssetCoordinates(asset, payload.du_id, {
                lat: payload.latitude,
                lng: payload.longitude,
              }),
            ),
          },
        );
      }
      return { prev };
    },
    onError: (_err, _payload, ctx) => {
      // Rollback optimistic update on error
      if (ctx?.prev) {
        qc.setQueryData([OPS_GEO_ASSETS_KEY, projectId], ctx.prev);
      }
    },
    onSettled: () => {
      // Always refetch to ensure server truth — but topology already updated optimistically
      void qc.invalidateQueries({ queryKey: [OPS_GEO_PLACEMENT_STATUS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_GEO_ASSETS_KEY] });
      void qc.invalidateQueries({
        queryKey: [OPS_PROJECT_NETWORK_TOPOLOGY_KEY],
      });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Unplace DU (remove from map)
// ---------------------------------------------------------------------------
export function useUnplaceDU(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: GeoUnplaceDUPayload) => {
      const { data } = await api.delete<ApiEnvelope<GeoUnplaceDUResult>>(
        `/ops/projects/${projectId}/geo-unplace-du/`,
        { data: payload },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_GEO_PLACEMENT_STATUS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_GEO_ASSETS_KEY] });
      void qc.invalidateQueries({
        queryKey: [OPS_PROJECT_NETWORK_TOPOLOGY_KEY],
      });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Create GEO Asset
// ---------------------------------------------------------------------------
export function useCreateGeoAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      project: string;
      name: string;
      asset_type: string;
      metadata?: Record<string, unknown>;
    }) => {
      const { data: resp } = await api.post<ApiEnvelope<OpsGeoAsset>>(
        "/geo/assets/",
        data,
      );
      return resp;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_GEO_ASSETS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Freeze GEO Baseline
// ---------------------------------------------------------------------------
export function useFreezeBaseline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      project: string;
      action?: "freeze" | "unfreeze";
    }) => {
      const { data: resp } = await api.post<
        ApiEnvelope<{
          id?: string;
          frozen: boolean;
          placements_count?: number;
          deleted?: number;
        }>
      >(
        `/ops/projects/${data.project}/freeze-baseline/`,
        data.action ? { action: data.action } : undefined,
      );
      return resp;
    },
    onSuccess: (_resp, data) => {
      void refreshProjectGates(qc, data.project);
      void qc.invalidateQueries({ queryKey: [OPS_GEO_BASELINES_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_GEO_ASSETS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_GEO_PLACEMENT_STATUS_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Approve Scope
// ---------------------------------------------------------------------------
export function useApproveScope() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (scopeId: string) => {
      const { data: resp } = await api.post<ApiEnvelope<OpsScope>>(
        `/projects/scopes/${scopeId}/approve/`,
      );
      return resp;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_SCOPES_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Read helpers for selectors
// ---------------------------------------------------------------------------
export function usePrograms() {
  return useQuery({
    queryKey: ["programs"],
    queryFn: async () => {
      const { data } = await api.get<
        ApiEnvelope<Array<{ id: string; name: string }>>
      >("/projects/programs/");
      return data;
    },
  });
}

export function useOrganizations() {
  return useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data } = await api.get<
        ApiEnvelope<Array<{ id: string; name: string; org_type: string }>>
      >("/organizations/organizations/", {
        params: { exclude_platform_owner: true, is_active: true },
      });
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// Hardware Catalog Browse — Factory-backed products grouped for Planning
// Source: /api/v1/ops/catalog-hw-browse/
// ---------------------------------------------------------------------------
export function useFactoryCatalogBrowse(params?: Record<string, string>) {
  return useQuery({
    queryKey: [OPS_FACTORY_CATALOG_BROWSE_KEY, params],
    queryFn: async () => {
      const { data } = await api.get<CatalogBrowseResponse>(
        "/ops/catalog-hw-browse/",
        params ? { params } : undefined,
      );
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// Catalog Browse — compatibility alias for Factory-backed products
// @deprecated Use `useFactoryCatalogBrowse` instead.
// ---------------------------------------------------------------------------
/** @deprecated Use {@link useFactoryCatalogBrowse} for the Factory-backed catalog. */
export function useCatalogBrowse() {
  return useFactoryCatalogBrowse();
}

/** @deprecated Use {@link useFactoryCatalogBrowse}; operational contracts are no longer a Planning catalog source. */
export function useOperationalContractBrowse(params?: Record<string, string>) {
  return useFactoryCatalogBrowse(params);
}

// ---------------------------------------------------------------------------
// Update Scope Line
// ---------------------------------------------------------------------------
export function useUpdateScopeLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: {
      id: string;
      quantity?: number;
      metadata?: Record<string, unknown>;
    }) => {
      const { data: resp } = await api.patch<ApiEnvelope<OpsScopeLine>>(
        `/projects/scope-lines/${id}/`,
        data,
      );
      return resp;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_SCOPE_LINES_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_SCOPES_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Delete Scope Line
// ---------------------------------------------------------------------------
export function useDeleteScopeLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/projects/scope-lines/${id}/`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_SCOPE_LINES_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_SCOPES_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Available Physical Assets (for assignment / substitution)
// ---------------------------------------------------------------------------
const OPS_EXTERNAL_LAYERS_KEY = "ops-external-layers";

/**
 * Units Garage says may be assigned to one deployment unit (SEC-D34 S3): its
 * availability projection, not the raw inventory. Disabled until a unit is known.
 */
export function useAvailableAssets(
  deploymentUnitId: string | null | undefined,
) {
  return useQuery({
    queryKey: [OPS_AVAILABLE_ASSETS_KEY, deploymentUnitId],
    enabled: Boolean(deploymentUnitId),
    // Availability moves with every reservation, and a hold must clear on the
    // next opening: never serve it from cache.
    staleTime: 0,
    queryFn: async (): Promise<AssetAvailability> => {
      try {
        const { data } = await api.get("/garage/availability-projection/", {
          params: { deployment_unit_id: deploymentUnitId },
        });
        const availability = normalizeAssetAvailability(data);
        if (!availability) throw new Error("Unexpected availability response");
        return availability;
      } catch (error) {
        const held = heldAssetAvailability(error);
        if (held) return held;
        throw error;
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Planner Garage: the organization's available devices, ready to place
// ---------------------------------------------------------------------------

export type { PlannerGarage, PlannerGarageAsset } from "./lib/plannerGarage";

/**
 * The planner Garage: units the organization holds, deployable, bound to no map
 * position and paired to no standing case position. One server-side rule
 * (`GET /cases/garage/available/`) shared with the field stock picker, so a unit
 * that is somewhere already is offered nowhere else.
 */
export function usePlannerGarage(
  projectId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: [
      OPS_AVAILABLE_ASSETS_KEY,
      OPS_ASSET_ASSIGNMENTS_KEY,
      "planner",
      projectId,
    ],
    enabled: enabled && Boolean(projectId),
    queryFn: async (): Promise<PlannerGarage> => {
      const { data } = await api.get<
        ApiEnvelope<
          | PlannerGarageAsset[]
          | { results: PlannerGarageAsset[]; count: number }
        >
      >("/cases/garage/available/", { params: { project: projectId } });
      return normalizePlannerGarage(data);
    },
  });
}

// ---------------------------------------------------------------------------
// Create Asset Assignment
// ---------------------------------------------------------------------------

export function useCreateAssetAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      du: string;
      asset: string;
      project: string;
    }) => {
      const { data: resp } = await api.post<
        ApiEnvelope<{ id: string; du: string; asset: string; project: string }>
      >("/operations/asset-assignments/", data);
      return resp;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_PROJECT_DUS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_AVAILABLE_ASSETS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_ASSET_ASSIGNMENTS_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Substitution
// ---------------------------------------------------------------------------
export function useSubstitution(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { du_id: string; new_asset_id: string }) => {
      const { data } = await api.post<ApiEnvelope<SubstitutionResult>>(
        `/ops/projects/${projectId}/substitution/`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_MAINTENANCE_SUMMARY_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_PROJECT_DUS_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_WORKSPACE_SUMMARY_KEY] });
      void qc.invalidateQueries({ queryKey: [OPS_BATCHES_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// External Layers (GEO)
// ---------------------------------------------------------------------------
export function useExternalLayers(projectId: string | undefined) {
  return useQuery({
    queryKey: [OPS_EXTERNAL_LAYERS_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<ExternalLayer[]>>(
        "/geo/external-layers/",
        { params: { project: projectId } },
      );
      return data;
    },
    enabled: !!projectId,
  });
}

export function useCreateExternalLayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await api.post<ApiEnvelope<ExternalLayer>>(
        "/geo/external-layers/",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_EXTERNAL_LAYERS_KEY] });
    },
  });
}

export function useUpdateExternalLayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: { id: string } & Record<string, unknown>) => {
      const { data: resp } = await api.patch<ApiEnvelope<ExternalLayer>>(
        `/geo/external-layers/${id}/`,
        data,
      );
      return resp;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_EXTERNAL_LAYERS_KEY] });
    },
  });
}

export function useDeleteExternalLayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/geo/external-layers/${id}/`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_EXTERNAL_LAYERS_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// Project Zones (GEO) — v0.11.7
// ---------------------------------------------------------------------------
const OPS_PROJECT_ZONES_KEY = "ops-project-zones";

export function useProjectZones(
  projectId: string | undefined,
  context?: TenantContextSnapshot,
) {
  const snapshot = Object.freeze({
    organization: context
      ? context.organization
      : localStorage.getItem("active_organization_id"),
    workspace: context
      ? context.workspace
      : localStorage.getItem("active_workspace_id"),
  });
  return useQuery({
    queryKey: [
      OPS_PROJECT_ZONES_KEY,
      projectId,
      snapshot.organization,
      snapshot.workspace,
      "complete",
    ],
    queryFn: ({ signal }) => fetchAllProjectZones(projectId!, snapshot, signal),
    enabled: !!projectId,
    retry: false,
    staleTime: 1000 * 60, // 1 min
  });
}

export function useCreateProjectZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (zone: {
      project: string;
      name: string;
      zone_type: string;
      color: string;
      /** Not sent for a trail or a point: the server derives it. */
      geometry?: GeoJSON.Geometry;
      source_geometry?: GeoJSON.LineString | GeoJSON.Point;
      shape?: Record<string, unknown>;
      properties?: Record<string, unknown>;
      parent?: string;
      floor_number?: number;
    }) => {
      const { data } = await api.post<ApiEnvelope<ProjectZone>>(
        "/geo/zones/",
        zone,
      );
      return data;
    },
    onSuccess: (_data, zone) => {
      void qc.invalidateQueries({ queryKey: [OPS_PROJECT_ZONES_KEY] });
      void refreshProjectGates(qc, zone.project);
    },
  });
}

export function useUpdateProjectZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: { id: string } & Record<string, unknown>) => {
      const { data: resp } = await api.patch<ApiEnvelope<ProjectZone>>(
        `/geo/zones/${id}/`,
        data,
      );
      return resp;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_PROJECT_ZONES_KEY] });
      void refreshProjectGates(qc);
    },
  });
}

export function useDeleteProjectZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/geo/zones/${id}/`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [OPS_PROJECT_ZONES_KEY] });
      void refreshProjectGates(qc);
    },
  });
}

// ---------------------------------------------------------------------------
// Service Keys (API key configuration)
// ---------------------------------------------------------------------------
const SERVICE_KEYS_KEY = "service-keys";

/** Status of a single external service key. */
export interface ServiceKeyStatus {
  service: string;
  is_active: boolean;
  configured: boolean;
  last_verified: string | null;
  description: string;
  masked_key: string | null;
}

/** Fetch status for all known external services. */
export function useServiceKeys() {
  return useQuery({
    queryKey: [SERVICE_KEYS_KEY],
    queryFn: async () => {
      const { data } =
        await api.get<ApiEnvelope<ServiceKeyStatus[]>>("/geo/service-keys/");
      return data.data;
    },
  });
}

/** Save (create or update) a service API key. */
export function useSaveServiceKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { service: string; api_key: string }) => {
      const { data } = await api.post<
        ApiEnvelope<{
          service: string;
          configured: boolean;
          is_active: boolean;
        }>
      >("/geo/service-keys/", payload);
      return data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [SERVICE_KEYS_KEY] });
      // Also invalidate weather tiles since their availability may change
      void qc.invalidateQueries({ queryKey: ["external-weather-tiles"] });
    },
  });
}

/** Delete a service API key. */
export function useDeleteServiceKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (service: string) => {
      await api.delete(`/geo/service-keys/${service}/`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [SERVICE_KEYS_KEY] });
      void qc.invalidateQueries({ queryKey: ["external-weather-tiles"] });
    },
  });
}

/** Verify an active provider credential without returning it to the browser. */
export function useVerifyServiceKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (service: string) => {
      const { data } = await api.post<
        ApiEnvelope<{
          service: string;
          configured: boolean;
          is_active: boolean;
          verified: boolean;
          last_verified: string;
        }>
      >(`/geo/service-keys/${service}/verify/`, {});
      return data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [SERVICE_KEYS_KEY] });
    },
  });
}

// ---------------------------------------------------------------------------
// External Data Services — Wave 2
// ---------------------------------------------------------------------------
const FIRMS_DATA_KEY = "external-firms-data";
const WEATHER_DATA_KEY = "external-weather-data";
const WEATHER_TILES_KEY = "external-weather-tiles";
const CLOUD_COVER_KEY = "external-cloud-cover";

/** FIRMS active fire tile URL + optional GeoJSON points. */
export function useFIRMSData(bbox?: {
  west: number;
  south: number;
  east: number;
  north: number;
}) {
  return useQuery({
    queryKey: [FIRMS_DATA_KEY, bbox],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (bbox) {
        params.west = String(bbox.west);
        params.south = String(bbox.south);
        params.east = String(bbox.east);
        params.north = String(bbox.north);
      }
      const { data } = await api.get<
        ApiEnvelope<{
          provider: string;
          tile_url: string;
          type: string;
          maxzoom?: number;
          minzoom?: number;
          attribution: string;
          description: string;
          geojson?: GeoJSON.FeatureCollection;
        }>
      >("/geo/external-data/firms/", { params });
      const inner = data.data;
      if (inner.tile_url) {
        // Fix WMTS template: replace {time} with today, swap {y}/{x} to {x}/{y}
        const today = new Date().toISOString().slice(0, 10);
        const directUrl = inner.tile_url
          .replace("{time}", today)
          .replace("{z}/{y}/{x}", "{z}/{x}/{y}");
        // Route through our backend proxy to bypass CORS.
        // Encode the source URL but preserve {z}/{x}/{y} as MapLibre placeholders.
        const baseUrl = api.defaults.baseURL ?? "/api/v1";
        const encodedSource = encodeURIComponent(
          directUrl
            .replace("{z}", "ZLEVEL")
            .replace("{x}", "XCOL")
            .replace("{y}", "YROW"),
        )
          .replace("ZLEVEL", "{z}")
          .replace("XCOL", "{x}")
          .replace("YROW", "{y}");
        inner.tile_url = `${baseUrl}/geo/external-data/tile-proxy/{z}/{x}/{y}.png?source=${encodedSource}`;
      }
      return inner;
    },
    staleTime: 1000 * 60 * 30, // 30 min
  });
}

/** Open-Meteo current weather for a coordinate. */
export function useWeatherData(lat: number | null, lng: number | null) {
  return useQuery({
    queryKey: [WEATHER_DATA_KEY, lat, lng],
    queryFn: async () => {
      const { data } = await api.get<
        ApiEnvelope<{
          provider: string;
          latitude: number;
          longitude: number;
          current: {
            temperature_2m: number;
            relative_humidity_2m: number;
            wind_speed_10m: number;
            wind_direction_10m: number;
            weather_code: number;
            cloud_cover: number;
            precipitation: number;
          };
          current_units: Record<string, string>;
        }>
      >("/geo/external-data/weather/", {
        params: { lat: String(lat), lng: String(lng) },
      });
      return data.data;
    },
    enabled: lat != null && lng != null,
    staleTime: 1000 * 60 * 10, // 10 min
  });
}

export interface CloudCoverBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface CloudCoverGridData {
  provider: "Open-Meteo";
  available: boolean;
  bounds: CloudCoverBounds;
  data_kind?: "modelled_current";
  geojson: GeoJSON.FeatureCollection<
    GeoJSON.Polygon,
    {
      cloud_cover_pct: number;
      observed_at: string | null;
      data_kind: "modelled_current";
      provider: "Open-Meteo";
    }
  >;
}

/** Current modelled cloud cover sampled across the visible map bounds. */
export function useCloudCoverGrid(
  bounds: CloudCoverBounds | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [CLOUD_COVER_KEY, bounds],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<CloudCoverGridData>>(
        "/geo/external-data/cloud-cover/",
        {
          params: {
            west: String(bounds!.west),
            south: String(bounds!.south),
            east: String(bounds!.east),
            north: String(bounds!.north),
          },
        },
      );
      return data.data;
    },
    enabled: enabled && bounds != null,
    staleTime: 1000 * 60 * 10,
  });
}

/** OpenWeatherMap tile URL patterns. */
export function useWeatherTiles() {
  return useQuery({
    queryKey: [WEATHER_TILES_KEY],
    queryFn: async () => {
      const { data } = await api.get<
        ApiEnvelope<{
          provider: string;
          available: boolean;
          message?: string;
          attribution?: string;
          layers: Array<{
            id: string;
            name: string;
            tile_url: string;
            type: string;
          }>;
        }>
      >("/geo/external-data/weather-tiles/");
      return data.data;
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

// ---------------------------------------------------------------------------
// External Data Services — Wave 3 (AEMET, NetworkCoverage, WMTS)
// ---------------------------------------------------------------------------
const AEMET_ALERTS_KEY = "external-aemet-alerts";
const AEMET_WEATHER_KEY = "external-aemet-weather";
const WMTS_CAPABILITIES_KEY = "external-wmts-capabilities";

export interface AEMETWeatherSummary {
  provider: "AEMET";
  type: "weather_summary";
  available?: boolean;
  message?: string;
  station?: string;
  station_id?: string;
  observation_time?: string;
  temperature_c?: number | null;
  humidity_pct?: number | null;
  wind_speed_kmh?: number | null;
  wind_direction_deg?: number | null;
  pressure_hpa?: number | null;
  precipitation_mm?: number | null;
}

/** AEMET weather alerts as GeoJSON features, fetched only for an area. */
export function useAEMETAlerts(area?: string) {
  return useQuery({
    queryKey: [AEMET_ALERTS_KEY, area],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (area) params.area = area;
      const { data } = await api.get<
        ApiEnvelope<{
          provider: string;
          type: string;
          available?: boolean;
          message?: string;
          geojson?: GeoJSON.FeatureCollection;
          count?: number;
        }>
      >("/geo/external-data/aemet/alerts/", { params });
      return data.data;
    },
    // No area, no request: the caller passes one only where AEMET answers.
    enabled: area != null,
    staleTime: 1000 * 60 * 15, // 15 min
  });
}

/**
 * Where a point is (W2): the country the backend's `region_of` finds there
 * and, per capability, the sources the provider policy names for it. The
 * frontend keeps no "is this Spain?" outline of its own.
 */
export interface GeoRegion {
  region: {
    status: "resolved" | "no_area" | "no_country";
    country: string | null;
    name: string | null;
    countries: string[];
    cross_border: boolean;
    subdivision: string | null;
    basis: string | null;
    source: string;
  };
  sources: Record<string, string[]>;
}

const GEO_REGION_KEY = "geo-region";

function boundedCoordinate(lat: number | null, lng: number | null) {
  return (
    lat != null &&
    lng != null &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

/** The country at a coordinate and the sources that answer there. */
export function useGeoRegion(lat: number | null, lng: number | null) {
  return useQuery({
    queryKey: [GEO_REGION_KEY, lat, lng],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<GeoRegion>>("/geo/region/", {
        params: { lat: String(lat), lng: String(lng) },
      });
      return data.data;
    },
    enabled: boundedCoordinate(lat, lng),
    // Borders do not move: a point's country is asked once per session.
    staleTime: Infinity,
  });
}

/** Whether the policy names a source for a capability at this region. */
export function regionHasSource(
  region: GeoRegion | undefined,
  capability: string,
  source: string,
): boolean {
  return region?.sources[capability]?.includes(source) ?? false;
}

/** AEMET weather summary for a coordinate, only where AEMET covers it. */
export function useAEMETWeather(lat: number | null, lng: number | null) {
  const { data: region } = useGeoRegion(lat, lng);
  return useQuery({
    queryKey: [AEMET_WEATHER_KEY, lat, lng],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<AEMETWeatherSummary>>(
        "/geo/external-data/aemet/",
        {
          params: { lat: String(lat), lng: String(lng) },
        },
      );
      return data.data;
    },
    enabled:
      boundedCoordinate(lat, lng) &&
      regionHasSource(region, "weather.observation", "aemet"),
    staleTime: 1000 * 60 * 30, // 30 min
  });
}

export type NetworkCoverageCoverageStatus =
  | "covered"
  | "not_covered"
  | "not_evaluable";

export interface NetworkCoverageGatewayResult {
  status: "ready" | "error" | "not_evaluable";
  gateway_deployment_unit_id: string;
  gateway_name: string;
  product_sku: string | null;
  profile_id: string | null;
  profile_label: string | null;
  interface: string | null;
  image_data_url?: string;
  image_sha256?: string;
  calculation_hash?: string;
  calculation_id?: string | null;
  bounds?: { north: number; south: number; east: number; west: number };
  radius_km?: number;
  /** Gateway antenna height used for this overlay. */
  tx_height_m?: number;
  /** @deprecated Not sent outside Command since SEC-D30b. */
  source_package_id?: string | null;
  /** @deprecated Not sent outside Command since SEC-D30b. */
  source_release_id?: string | null;
  /** @deprecated Not sent outside Command since SEC-D30b. */
  source_hash?: string | null;
  code?: string;
  reason?: string;
  required_fields?: string[];
}

export interface NetworkCoverageDeviceResult {
  deployment_unit_id: string;
  device_name: string;
  product_sku: string | null;
  interface: string;
  status: NetworkCoverageCoverageStatus;
  best_gateway_deployment_unit_id: string | null;
  best_gateway_name: string | null;
  received_power_dbm: number | null;
  receiver_sensitivity_dbm: number | null;
  link_margin_db: number | null;
  /** Antenna heights of the best evaluated link. */
  tx_height_m?: number;
  rx_height_m?: number;
  evaluated_gateway_count: number;
  gateway_profile_id?: string;
  receiver_profile_id?: string;
  /** @deprecated Not sent outside Command since SEC-D30b. */
  transmitter_source_hash?: string | null;
  /** @deprecated Not sent outside Command since SEC-D30b. */
  receiver_source_hash?: string | null;
  calculation_hash?: string;
  reason: string | null;
  required_fields: string[];
}

export interface NetworkCoverageProjectCoverageResponse {
  contract: "sentinels.frontier.network-coverage.v1";
  project_id: string;
  alternative_id: string;
  generated_at: string;
  planning_defaults: {
    /** Defaults for devices without a planner installation height. */
    tx_height_m: number;
    rx_height_m: number;
    height_basis?: string;
    resolution_m: number;
    radius_basis: string;
  };
  gateway_results: NetworkCoverageGatewayResult[];
  device_results: NetworkCoverageDeviceResult[];
  summary: {
    positioned_device_count: number;
    gateway_count: number;
    calculated_gateway_count: number;
    coverage_overlay_count: number;
    gateway_error_count: number;
    covered_count: number;
    not_covered_count: number;
    not_evaluable_count: number;
    compatible_link_count?: number;
    evaluated_link_count?: number;
    link_evaluation_limited?: boolean;
  };
}

/** One project-level action: discover every gateway and classify every device. */
export function useNetworkCoverageProjectCoverage() {
  return useMutation({
    mutationFn: async (params: {
      project_id: string;
      alternative_id: string;
    }) => {
      const { data } = await api.post<
        ApiEnvelope<NetworkCoverageProjectCoverageResponse>
      >("/geo/external-data/network-coverage/project/", params);
      return data.data;
    },
  });
}

export type ProjectDispersionDeviceStatus =
  | "detectable"
  | "below_threshold"
  | "not_evaluable";

export interface ProjectDispersionRequest {
  project_id: string;
  alternative_id: string;
  source_lat: number;
  source_lng: number;
  emission_rate_gs: number;
  wind_speed_ms: number;
  wind_direction_deg: number;
  gas_name: string;
  source_height_m?: number;
  stability_class?: "A" | "B" | "C" | "D" | "E" | "F";
  receptor_height_m?: number;
  analysis_radius_km?: number;
  grid_resolution_m?: number;
}

export interface ProjectDispersionResponse {
  contract: "sentinels.frontier.project-dispersion.v1";
  provider: "Core Gaussian plume";
  project_id: string;
  alternative_id: string;
  generated_at: string;
  calculation_hash: string;
  model: {
    name: "steady_state_gaussian_plume";
    purpose: "planning_estimate";
    terrain_correction: false;
    live_detection: false;
  };
  scenario: Omit<ProjectDispersionRequest, "project_id" | "alternative_id"> & {
    source_height_m: number;
    stability_class: "A" | "B" | "C" | "D" | "E" | "F";
    receptor_height_m: number;
    analysis_radius_km: number;
    grid_resolution_m: number;
  };
  defaulted_fields: string[];
  result: {
    concentration_grid: number[][];
    grid_bounds: {
      min_lat: number;
      max_lat: number;
      min_lng: number;
      max_lng: number;
    };
    grid_rows: number;
    grid_cols: number;
    max_concentration: number;
    receptor_height_m: number;
  };
  device_results: Array<{
    deployment_unit_id: string;
    device_name: string;
    product_sku: string | null;
    latitude: number;
    longitude: number;
    status: ProjectDispersionDeviceStatus;
    concentration_ugm3: number;
    detection_threshold_ugm3: number | null;
    threshold_basis: "factory_product_profile" | null;
    reason: string | null;
    required_fields: string[];
    /** @deprecated Not sent outside Command since SEC-D30b. */
    source_package_id?: string | null;
    /** @deprecated Not sent outside Command since SEC-D30b. */
    source_release_id?: string | null;
    /** @deprecated Not sent outside Command since SEC-D30b. */
    source_hash?: string | null;
  }>;
  summary: {
    positioned_device_count: number;
    receptor_count: number;
    detectable_count: number;
    below_threshold_count: number;
    not_evaluable_count: number;
  };
}

export function useProjectDispersion() {
  return useMutation({
    mutationFn: async (params: ProjectDispersionRequest) => {
      const { data } = await api.post<ApiEnvelope<ProjectDispersionResponse>>(
        "/sak-geo/project-dispersion/",
        params,
      );
      return data.data;
    },
  });
}

/** WMTS GetCapabilities → parsed layers. */
export function useWMTSCapabilities(url: string | null) {
  return useQuery({
    queryKey: [WMTS_CAPABILITIES_KEY, url],
    queryFn: async () => {
      const { data } = await api.get<
        ApiEnvelope<{
          provider: string;
          service_url: string;
          layers: Array<{
            id: string;
            title: string;
            tileMatrixSet: string;
            format: string;
            url_template: string;
          }>;
        }>
      >("/geo/external-data/wmts-capabilities/", {
        params: { url: url! },
      });
      return data.data;
    },
    enabled: !!url,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });
}

// ---------------------------------------------------------------------------
// External Data Services — Wave 4 (AQI, Windy, Weather Alerts)
// ---------------------------------------------------------------------------
const AQI_DATA_KEY = "external-aqi-data";
const AQI_STATIONS_KEY = "external-aqi-stations";
const WINDY_DATA_KEY = "external-windy-data";
const WEATHER_ALERTS_KEY = "external-weather-alerts";

/** AQI classification type returned by the backend. */
export interface AQIClassification {
  label: string;
  label_es: string;
  color: string;
}

/** Open-Meteo AQI data for a coordinate. */
export function useAQIData(lat: number | null, lng: number | null) {
  return useQuery({
    queryKey: [AQI_DATA_KEY, lat, lng],
    queryFn: async () => {
      const { data } = await api.get<
        ApiEnvelope<{
          provider: string;
          latitude: number;
          longitude: number;
          us_aqi: number | null;
          european_aqi: number | null;
          classification: AQIClassification;
          pollutants: {
            pm2_5: number | null;
            pm10: number | null;
            ozone: number | null;
            nitrogen_dioxide: number | null;
            sulphur_dioxide: number | null;
            carbon_monoxide: number | null;
          };
          current: Record<string, number | null>;
          current_units: Record<string, string>;
        }>
      >("/geo/external-data/aqi/", {
        params: { lat: String(lat), lng: String(lng) },
      });
      return data.data;
    },
    enabled: lat != null && lng != null,
    staleTime: 1000 * 60 * 15, // 15 min
  });
}

/** OpenAQ nearby AQI monitoring stations as GeoJSON. */
export function useAQIStations(lat: number | null, lng: number | null) {
  return useQuery({
    queryKey: [AQI_STATIONS_KEY, lat, lng],
    queryFn: async () => {
      const { data } = await api.get<
        ApiEnvelope<{
          provider: string;
          type: "FeatureCollection";
          features: Array<{
            type: "Feature";
            geometry: { type: "Point"; coordinates: [number, number] };
            properties: {
              name: string;
              id: number;
              country: string;
              readings: Record<
                string,
                { value: number | null; unit: string; datetime: string }
              >;
              estimated_aqi: number | null;
              classification: AQIClassification;
            };
          }>;
          count: number;
        }>
      >("/geo/external-data/aqi-stations/", {
        params: { lat: String(lat), lng: String(lng) },
      });
      return data.data;
    },
    enabled: lat != null && lng != null,
    staleTime: 1000 * 60 * 30, // 30 min
  });
}

/** Windy embed URL + Open-Meteo wind forecast. */
export function useWindyData(lat: number | null, lng: number | null) {
  return useQuery({
    queryKey: [WINDY_DATA_KEY, lat, lng],
    queryFn: async () => {
      const { data } = await api.get<
        ApiEnvelope<{
          provider: string;
          embed_url: string;
          has_api_key: boolean;
          wind_data: {
            speed_kmh: number | null;
            direction_deg: number | null;
            gusts_kmh: number | null;
          };
          forecast_hours: Array<{
            time: string | null;
            speed_kmh: number | null;
            direction_deg: number | null;
            gusts_kmh: number | null;
          }>;
        }>
      >("/geo/external-data/windy/", {
        params: { lat: String(lat), lng: String(lng) },
      });
      return data.data;
    },
    enabled: lat != null && lng != null,
    staleTime: 1000 * 60 * 10, // 10 min
  });
}

/** Consolidated weather alerts from multiple sources. */
export interface WeatherAlert {
  source: string;
  /** "aemet" | "meteoalarm" | "nws" | "open-meteo" (R13). */
  provider?: string;
  /** Open-Meteo forecast items: the WMO weather code, for translation. */
  wmo_code?: number;
  /** A country-wide warning naming the point's own area; null if unknown. */
  in_project_area?: boolean | null;
  severity: "advisory" | "warning" | "severe";
  headline: string;
  description: string;
  event: string;
  effective: string;
  expires: string;
  area_description: string;
  area_geojson: GeoJSON.Geometry | null;
}

/**
 * Whether the national warning agency of the point's country answered (R13):
 * AEMET in Spain, MeteoAlarm in the rest of Europe, the NWS in the US.
 * ``unavailable`` means the warnings are unknown, never zero.
 */
export interface OfficialWeatherAlerts {
  status: "available" | "unavailable";
  provider: string | null;
  country: string | null;
  subdivision: string | null;
  reason: "outside_coverage" | "provider_error" | "not_configured" | null;
  /**
   * Why a ``provider_error`` happened, in one stable word (F-16):
   * ``connect_timeout``, ``timeout``, ``dns``, ``tls``, ``proxy``,
   * ``connection``, ``http_<status>``, ``too_large``, ``too_slow``,
   * ``invalid_feed``, ``no_feed``…
   */
  detail?: string | null;
  scope: "point" | "country" | null;
  count: number | null;
  elsewhere_count: number;
  /** MeteoAlarm answered because this agency could not (AEMET in Spain). */
  fallback_from?: string | null;
  fallback_reason?: OfficialWeatherAlerts["reason"];
  fallback_detail?: string | null;
}

export interface WeatherAlertsData {
  provider: string;
  alerts: WeatherAlert[];
  official?: OfficialWeatherAlerts;
  summary: {
    total: number;
    by_severity: Record<string, number>;
  };
}

export function useWeatherAlerts(lat: number | null, lng: number | null) {
  return useQuery({
    queryKey: [WEATHER_ALERTS_KEY, lat, lng],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<WeatherAlertsData>>(
        "/geo/external-data/weather-alerts/",
        {
          params: { lat: String(lat), lng: String(lng) },
        },
      );
      return data.data;
    },
    enabled: lat != null && lng != null,
    staleTime: 1000 * 60 * 15, // 15 min
  });
}

// ---------------------------------------------------------------------------
// External Data Services — Wave 5 (One Call 3.0, Capabilities, Area Intelligence)
// ---------------------------------------------------------------------------
const ONE_CALL_KEY = "external-one-call";
const WEATHER_CAPABILITIES_KEY = "external-weather-capabilities";
const HISTORICAL_CONTEXT_KEY = "external-historical-context";
const AREA_INTELLIGENCE_KEY = "external-area-intelligence";

/** One Call 3.0 hourly forecast entry. */
export interface OneCallHourly {
  time: string | number | null;
  temp_c: number | null;
  feels_like_c: number | null;
  humidity_pct: number | null;
  wind_speed_kmh: number | null;
  wind_direction_deg: number | null;
  precipitation_mm: number | null;
  cloud_cover_pct: number | null;
  weather_code: number | null;
  weather_description: string;
}

/** One Call 3.0 daily forecast entry. */
export interface OneCallDaily {
  date: string | number | null;
  temp_min_c: number | null;
  temp_max_c: number | null;
  precipitation_mm: number | null;
  wind_speed_kmh: number | null;
  weather_code: number | null;
  weather_description: string;
}

/** One Call 3.0 severe alert. */
export interface OneCallAlert {
  source: string;
  event: string;
  start: number | null;
  end: number | null;
  description: string;
  severity: string;
}

/** One Call 3.0 current conditions. */
export interface OneCallCurrentConditions {
  temp_c: number | null;
  feels_like_c: number | null;
  humidity_pct: number | null;
  wind_speed_kmh: number | null;
  wind_direction_deg: number | null;
  cloud_cover_pct: number | null;
  uv_index: number | null;
  visibility_m: number | null;
  weather_code: number | null;
  weather_description: string;
}

/** Full One Call 3.0 response. */
export interface OneCallData {
  provider: string;
  provider_type: string;
  error?: "upstream_unavailable";
  latitude: number;
  longitude: number;
  current_conditions: OneCallCurrentConditions | null;
  forecast_window: {
    hourly_48h: OneCallHourly[];
    daily_7d: OneCallDaily[];
  } | null;
  severe_alerts: OneCallAlert[];
  historical_context: null;
}

/** Fetch OWM One Call 3.0 data (4 product classes). */
export function useOneCallData(lat: number | null, lng: number | null) {
  // OWM's descriptions and alerts come in the language on screen.
  const { language } = useI18n();
  return useQuery({
    queryKey: [ONE_CALL_KEY, lat, lng, language],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<OneCallData>>(
        "/geo/external-data/one-call/",
        { params: { lat: String(lat), lng: String(lng), lang: language } },
      );
      return data.data;
    },
    enabled: lat != null && lng != null,
    staleTime: 1000 * 60 * 10, // 10 min
  });
}

/** Provider capability matrix entry. */
export interface WeatherCapabilityEntry {
  provider: string;
  coverage: string;
  supports_live: boolean;
  supports_forecast: boolean;
  supports_historical: boolean;
  supports_alerts: boolean;
  temporal_granularity: string;
  geo_granularity: string;
  authority_level: string;
  requires_api_key: boolean;
  endpoint: string;
}

/** Fetch provider capability matrix. */
export function useWeatherCapabilities() {
  return useQuery({
    queryKey: [WEATHER_CAPABILITIES_KEY],
    queryFn: async () => {
      const { data } = await api.get<
        ApiEnvelope<{
          provider: string;
          version: string;
          capabilities: WeatherCapabilityEntry[];
        }>
      >("/geo/external-data/weather-capabilities/");
      return data.data;
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 hours — static
  });
}

/** Historical daily baseline entry. */
export interface HistoricalDayBaseline {
  date: string;
  source: string;
  avg_temp_c: number | null;
  max_temp_c: number | null;
  min_temp_c: number | null;
  total_precipitation_mm: number;
  avg_wind_kmh: number | null;
  avg_cloud_pct: number | null;
  error?: string;
}

/** Fetch 5-day historical context baselines. */
export function useHistoricalContext(lat: number | null, lng: number | null) {
  return useQuery({
    queryKey: [HISTORICAL_CONTEXT_KEY, lat, lng],
    queryFn: async () => {
      const { data } = await api.get<
        ApiEnvelope<{
          provider: string;
          latitude: number;
          longitude: number;
          window_days: number;
          daily_baselines: HistoricalDayBaseline[];
        }>
      >("/geo/external-data/historical-context/", {
        params: { lat: String(lat), lng: String(lng) },
      });
      return data.data;
    },
    enabled: lat != null && lng != null,
    staleTime: 1000 * 60 * 30, // 30 min
  });
}

/** Area intelligence report. */
export interface AreaIntelligenceReport {
  provider: string;
  recommended_provider: string;
  project_id: string;
  latitude: number;
  longitude: number;
  admin_context: {
    country_code: string | null;
    /** The country's English name (Natural Earth), if the point is in one. */
    country_name?: string | null;
    region_hint: string;
  };
  current_conditions: OneCallCurrentConditions | null;
  forecast_summary: {
    window_hours: number;
    temp_min_c: number | null;
    temp_max_c: number | null;
    total_precipitation_mm: number;
    max_wind_kmh: number | null;
  } | null;
  active_alerts: OneCallAlert[];
  historical_baselines: HistoricalDayBaseline[];
}

/** Fetch area intelligence report for a project.
 *  Accepts optional lat/lng fallback for when the project has no sites yet.
 */
export function useAreaIntelligence(
  projectId: string | undefined,
  fallbackLat?: number | null,
  fallbackLng?: number | null,
) {
  return useQuery({
    queryKey: [AREA_INTELLIGENCE_KEY, projectId, fallbackLat, fallbackLng],
    queryFn: async () => {
      const params: Record<string, string> = { project_id: projectId! };
      if (fallbackLat != null && fallbackLng != null) {
        params.lat = String(fallbackLat);
        params.lng = String(fallbackLng);
      }
      const { data } = await api.get<ApiEnvelope<AreaIntelligenceReport>>(
        "/geo/external-data/area-intelligence/",
        { params },
      );
      return data.data;
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 15, // 15 min
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Climatology Report (10-year monthly averages)
// ---------------------------------------------------------------------------
const CLIMATOLOGY_KEY = "ops-climatology-v2";

export interface ClimatologyMonth {
  month: number;
  month_name: string;
  avg_temp_c: number | null;
  avg_max_temp_c: number | null;
  avg_min_temp_c: number | null;
  avg_precipitation_mm: number | null;
  total_precipitation_mm: number | null;
  avg_precip_per_year_mm: number | null;
  avg_precip_days_per_year: number;
  avg_wind_speed_kmh: number | null;
  dominant_wind_direction: string | null;
  avg_cloud_cover_pct: number | null;
  avg_solar_radiation_mj: number | null;
  avg_sunshine_hours: number | null;
  avg_peak_sun_hours: number | null;
}

export interface ClimatologyReport {
  latitude: number;
  longitude: number;
  years_analyzed: number;
  period: string;
  source: string;
  /** The reanalysis behind it (``era5``), to name it in the reader's language. */
  model?: string;
  monthly: ClimatologyMonth[];
  annual_summary: {
    avg_temp_c: number | null;
    total_precipitation_mm_per_year: number | null;
    avg_precip_days_per_year: number;
    avg_wind_speed_kmh: number | null;
    avg_cloud_cover_pct: number | null;
    avg_solar_radiation_mj: number | null;
    avg_sunshine_hours: number | null;
    avg_peak_sun_hours: number | null;
  };
  error?: string;
}

/** Fetch climatology report (monthly averages over N years). */
export function useClimatology(
  lat: number | null,
  lng: number | null,
  years: number = 10,
) {
  const latitude = lat == null ? null : Math.round(lat * 100) / 100;
  const longitude = lng == null ? null : Math.round(lng * 100) / 100;
  const location =
    latitude == null || longitude == null ? null : `${latitude},${longitude}`;
  const [settledLocation, setSettledLocation] = useState<string | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => setSettledLocation(location), 600);
    return () => clearTimeout(timer);
  }, [location]);
  const settling = location !== settledLocation;
  const query = useQuery({
    queryKey: [CLIMATOLOGY_KEY, latitude, longitude, years],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<ClimatologyReport>>(
        "/geo/external-data/climatology/",
        {
          params: {
            lat: String(latitude),
            lng: String(longitude),
            years: String(years),
          },
          timeout: 40000,
        },
      );
      // A legacy/proxy HTTP 200 error must never become a fresh 24-hour success.
      if (
        !data.data ||
        data.data.error ||
        data.data.monthly?.length !== 12 ||
        !data.data.annual_summary
      ) {
        throw new Error("Climatology unavailable");
      }
      return data.data;
    },
    enabled: location != null && !settling,
    staleTime: 1000 * 60 * 60 * 24,
    retry: false,
    refetchOnWindowFocus: false,
  });
  return {
    ...query,
    isLoading: query.isLoading || (location != null && settling),
  };
}

// ---------------------------------------------------------------------------
// Bulk Provision Scope
// ---------------------------------------------------------------------------
export function useBulkProvisionScope(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (scopeId: string) => {
      const { data } = await api.post<
        ApiEnvelope<{ total_dus: number; requests_created: number }>
      >(`/operations/deployment-units/bulk-provision/`, { scope_id: scopeId });
      return data;
    },
    onSuccess: () => {
      // Invalidate all related queries to force refetch
      void qc.invalidateQueries({ queryKey: [OPS_PROJECT_DUS_KEY, projectId] });
      void qc.invalidateQueries({ queryKey: [OPS_GEO_ASSETS_KEY, projectId] });
      void qc.invalidateQueries({ queryKey: [OPS_TWIN_UNITS_KEY, projectId] });
      void qc.invalidateQueries({
        queryKey: [OPS_WORKSPACE_SUMMARY_KEY, projectId],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Telemetry — live device data from ThingsBoard via proxy
// ---------------------------------------------------------------------------

export function useDeviceLatestTelemetry(deviceId: string | undefined) {
  return useQuery({
    queryKey: [TELEMETRY_LATEST_KEY, deviceId],
    queryFn: async () => {
      const { data } = await api.get<
        ApiEnvelope<Record<string, TelemetryValue>>
      >(`/twins/telemetry/${deviceId}/latest/`);
      return data;
    },
    enabled: !!deviceId,
    refetchInterval: 30_000,
  });
}

export function useDeviceTelemetryHistory(
  deviceId: string | undefined,
  keys: string[],
  timeRange: "1h" | "6h" | "24h" | "7d" = "1h",
) {
  const rangeMs: Record<string, number> = {
    "1h": 3_600_000,
    "6h": 21_600_000,
    "24h": 86_400_000,
    "7d": 604_800_000,
  };
  const endTs = Date.now();
  const startTs = endTs - (rangeMs[timeRange] ?? 3_600_000);

  return useQuery({
    queryKey: [TELEMETRY_HISTORY_KEY, deviceId, keys.join(","), timeRange],
    queryFn: async () => {
      const params = new URLSearchParams({
        keys: keys.join(","),
        start_ts: String(startTs),
        end_ts: String(endTs),
        limit: "500",
      });
      const { data } = await api.get<ApiEnvelope<TelemetryHistory>>(
        `/twins/telemetry/${deviceId}/history/?${params.toString()}`,
      );
      return data;
    },
    enabled: !!deviceId && keys.length > 0,
    refetchInterval: 30_000,
  });
}

export function useFleetLatestTelemetry(projectId: string | undefined) {
  return useQuery({
    queryKey: [TELEMETRY_FLEET_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<DeviceTelemetry[]>>(
        `/twins/telemetry/fleet/${projectId}/latest/`,
      );
      return data;
    },
    enabled: !!projectId,
    refetchInterval: 30_000,
  });
}

export function usePlatformHealth(projectId: string | undefined) {
  return useQuery({
    queryKey: [PLATFORM_HEALTH_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<PlatformHealth>>(
        `/twins/platform-health/${projectId}/`,
      );
      return data;
    },
    enabled: !!projectId,
    refetchInterval: 10_000,
  });
}

export function useSimulationStatus(projectId: string | undefined) {
  return useQuery({
    queryKey: [SIMULATION_STATUS_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<SimulationStatus>>(
        `/twins/simulation/${projectId}/status/`,
      );
      return data;
    },
    enabled: !!projectId,
    refetchInterval: 10_000,
  });
}

export function useStartSimulation(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<ApiEnvelope<SimulationStatus>>(
        `/twins/simulation/${projectId}/start/`,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [SIMULATION_STATUS_KEY, projectId],
      });
      void qc.invalidateQueries({ queryKey: [PLATFORM_HEALTH_KEY, projectId] });
    },
  });
}

export function useStopSimulation(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<ApiEnvelope<SimulationStatus>>(
        `/twins/simulation/${projectId}/stop/`,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [SIMULATION_STATUS_KEY, projectId],
      });
      void qc.invalidateQueries({ queryKey: [PLATFORM_HEALTH_KEY, projectId] });
    },
  });
}

export function useRestartSimulation(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<ApiEnvelope<SimulationStatus>>(
        `/twins/simulation/${projectId}/restart/`,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [SIMULATION_STATUS_KEY, projectId],
      });
      void qc.invalidateQueries({ queryKey: [PLATFORM_HEALTH_KEY, projectId] });
      void qc.invalidateQueries({ queryKey: [TELEMETRY_FLEET_KEY, projectId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Simulation Scenarios (v0.11.8)
// ---------------------------------------------------------------------------
const SCENARIO_STATUS_KEY = "simulation-scenario";

export function useScenarioStatus(projectId: string | undefined) {
  return useQuery({
    queryKey: [SCENARIO_STATUS_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<
        ApiEnvelope<{ scenario: string; device_name: string | null }>
      >(`/twins/scenarios/${projectId}/`);
      return data.data;
    },
    enabled: !!projectId,
    refetchInterval: 5000,
  });
}

export function useSetScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      scenario,
      deviceName,
    }: {
      projectId: string;
      scenario: string;
      deviceName?: string;
    }) => {
      const { data } = await api.post<
        ApiEnvelope<{ scenario: string; device_name: string | null }>
      >(`/twins/scenarios/${projectId}/`, {
        scenario,
        device_name: deviceName,
      });
      return data;
    },
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({
        queryKey: [SCENARIO_STATUS_KEY, vars.projectId],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Hierarchy + Aggregation hooks (v0.11.0)
// ---------------------------------------------------------------------------

const FLEET_REAL_AVERAGES_KEY = "fleet-real-averages";
const FLEET_HIERARCHY_KEY = "fleet-hierarchy";
const FLEET_AGGREGATION_KEY = "fleet-aggregation";

export function useFleetRealAverages(projectId: string | undefined) {
  return useQuery({
    queryKey: [FLEET_REAL_AVERAGES_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get(
        `/twins/telemetry/fleet/${projectId}/real-averages/`,
      );
      return data.data;
    },
    enabled: !!projectId,
    refetchInterval: 60_000, // 1 min
    retry: 1,
  });
}

export function useFleetHierarchy(projectId: string | undefined) {
  return useQuery({
    queryKey: [FLEET_HIERARCHY_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<FleetHierarchy>>(
        `/twins/telemetry/hierarchy/${projectId}/`,
      );
      return data;
    },
    enabled: !!projectId,
    refetchInterval: 30_000,
  });
}

export function useFleetAggregation(projectId: string | undefined) {
  return useQuery({
    queryKey: [FLEET_AGGREGATION_KEY, projectId],
    queryFn: async () => {
      const { data } = await api.get<ApiEnvelope<FleetAggregation>>(
        `/twins/telemetry/aggregation/${projectId}/`,
      );
      return data;
    },
    enabled: !!projectId,
    refetchInterval: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Layer Catalog & Provider Registry (Wave 3 — GEO-EVO-I Phase G, #182/#186)
// ---------------------------------------------------------------------------
const LAYER_CATALOG_KEY = "geo-layer-catalog";
const PROVIDER_REGISTRY_KEY = "geo-provider-registry";

/**
 * Fetch the backend layer catalog.
 * Supports optional category/provider/phenomenon filtering.
 */
export function useLayerCatalog(filters?: {
  category?: string;
  provider?: string;
  phenomenon?: string;
  is_builtin?: boolean;
}) {
  return useQuery({
    queryKey: [LAYER_CATALOG_KEY, filters],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (filters?.category) params.category = filters.category;
      if (filters?.provider) params.provider = filters.provider;
      if (filters?.phenomenon) params.phenomenon = filters.phenomenon;
      if (filters?.is_builtin !== undefined)
        params.is_builtin = String(filters.is_builtin);
      const { data } = await api.get<LayerCatalogEntry[]>(
        "/geo/layers/catalog/",
        { params },
      );
      // The catalog endpoint returns a raw array (not envelope-wrapped)
      return Array.isArray(data)
        ? data
        : ((data as unknown as { data: LayerCatalogEntry[] }).data ?? []);
    },
    staleTime: 1000 * 60 * 5, // 5 min — catalog rarely changes
  });
}

/** Fetch the provider registry summary. */
export function useProviderRegistry() {
  return useQuery({
    queryKey: [PROVIDER_REGISTRY_KEY],
    queryFn: async () => {
      const { data } =
        await api.get<GeoProviderRegistryResponse>("/geo/providers/");
      return data;
    },
    staleTime: 1000 * 60 * 30, // 30 min — providers are semi-static
  });
}

// ---------------------------------------------------------------------------
// GeoElement CRUD (GEO EVO II — Wave 2)
// ---------------------------------------------------------------------------

const GEO_ELEMENTS_KEY = "geo-elements";
const GEO_ELEMENTS_GEOJSON_KEY = "geo-elements-geojson";

export function useCreateGeoElement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { data } = await api.post("/geo/elements/", payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [GEO_ELEMENTS_KEY] });
      qc.invalidateQueries({ queryKey: [GEO_ELEMENTS_GEOJSON_KEY] });
    },
  });
}

export function useDeleteGeoElement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/geo/elements/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [GEO_ELEMENTS_KEY] });
      qc.invalidateQueries({ queryKey: [GEO_ELEMENTS_GEOJSON_KEY] });
    },
  });
}

/**
 * Forward geocoding via backend — provider-agnostic.
 * Returns a mutation so callers can trigger searches imperatively.
 */
export function useGeoSearchForward() {
  return useMutation({
    mutationFn: async (params: {
      query: string;
      limit?: number;
      language?: string;
    }) => {
      const { data } = await api.post<GeoSearchResponse>(
        "/geo/search/forward/",
        {
          query: params.query,
          limit: params.limit ?? 5,
          language: params.language ?? "es",
        },
      );
      return data;
    },
  });
}

/**
 * Reverse geocoding via backend — provider-agnostic.
 */
export function useGeoSearchReverse() {
  return useMutation({
    mutationFn: async (params: {
      lat: number;
      lon: number;
      limit?: number;
    }) => {
      const { data } = await api.post<GeoSearchResponse>(
        "/geo/search/reverse/",
        {
          lat: params.lat,
          lon: params.lon,
          limit: params.limit ?? 1,
        },
      );
      return data;
    },
  });
}
